import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import ScrapeApi from '../../../../api/ScrapeApi'
import { intersection, isMFNLink } from './utils'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { RegisteredTextField } from '../../../../components/fields/TextField'
import { SourceTypeSelect } from './AdminSourceTypeSelect'
import { RegisteredDateTimePicker } from '../../../../components/fields/DateTimePicker'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import { PressReleaseLight, TransactionType, WarrantListItem } from '@finance-lab/utils/types'
import {
    singularConvertibleSourceTypes,
    singularIssueSourceTypes,
    singularWarrantSourceTypes,
} from '../../../../utils/issues/sourceTypes'
import { SourceType } from 'types/SourceType'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { SourceForm } from '../../types/SourceForm'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { SourceInputMethod } from '.'
import { SelectPressReleaseDialog } from './AdminSelectPressReleaseDialog'
import { usePressReleaseSelectDialog } from '../../../components/press-releases/utils'
import { useTranslation } from 'react-i18next'

export const getMinDate = (
    sourceType: SourceType,
    publicationDate: Date | null,
    sources: Array<Pick<SourceForm, 'sourceType' | 'publishedDate'>>,
    issue?: unknown,
) => {
    if (!issue || !sourceType) return publicationDate

    if (sourceType === 'Terms') {
        return publicationDate
    }

    const termsSource = sources.find((s) => s.sourceType === 'Terms')

    if (sourceType === 'Outcome' && termsSource?.publishedDate) {
        return new Date(termsSource.publishedDate)
    }

    return publicationDate
}

export const getMaxDate = (
    sourceType: SourceType,
    sources: Array<Pick<SourceForm, 'sourceType' | 'publishedDate'>>,
    issue?: unknown,
) => {
    if (!issue || !sourceType) return null

    if (sourceType === 'Outcome') return null

    const outcomeSource = sources.find((s) => s.sourceType === 'Outcome')
    if (sourceType === 'Terms') {
        if (outcomeSource?.publishedDate) {
            return new Date(outcomeSource.publishedDate)
        }
        return null
    }

    if (sourceType === 'Publication') {
        const termsSource = sources.find((s) => s.sourceType === 'Terms')

        if (termsSource?.publishedDate) {
            return new Date(termsSource.publishedDate)
        }

        if (outcomeSource?.publishedDate) {
            return new Date(outcomeSource.publishedDate)
        }
    }

    return null
}

export function SourceFormRow({
    onSubmit,
    inputMethod,
    issue,
    warrant,
    convertible,
    sources,
}: {
    inputMethod: SourceInputMethod
    onSubmit: (val: SourceForm) => void
    issue?: FormIssueEntity
    warrant?: WarrantListItem
    convertible?: ConvertibleEntity
    sources: Array<Pick<SourceForm, 'sourceType' | 'sourceUrl' | 'publishedDate'>>
}) {
    // https://github.com/react-hook-form/react-hook-form/issues/8510
    const defaultValues = {
        sourceUrl: '',
        internalComment: '',
        title: '',
        publishedDate: null,
        prId: null,
    }
    const { open: openSelectPressReleaseDialog } = usePressReleaseSelectDialog()
    const { t } = useTranslation()
    const formMethods = useForm<SourceForm>({ defaultValues, mode: 'onBlur' })
    const { setValue } = formMethods
    const [scraping, setScraping] = useState(false)
    const [selectedPressRelease, setSelectedPressRelease] = useState<PressReleaseLight | null>(null)
    const entity = issue || warrant || convertible
    const sourceType = formMethods.watch('sourceType')

    const publicationSource = sources.find((s) => s.sourceType === 'Publication')
    const pubDate =
        publicationSource?.publishedDate ||
        issue?.publicationDate ||
        warrant?.publicationDate ||
        convertible?.publicationDate ||
        null

    const publicationDate = pubDate ? new Date(pubDate) : null

    const minDate = getMinDate(sourceType, publicationDate, sources, issue)
    const maxDate = getMaxDate(sourceType, sources, issue)

    useEffect(() => {
        if (inputMethod === 'manual') {
            setSelectedPressRelease(null)
        }
    }, [formMethods, inputMethod])

    useEffect(() => {
        formMethods.setValue('prId', selectedPressRelease?.id ?? null)
    }, [formMethods, selectedPressRelease?.id])

    // Scrape data and update sourceUrl in the form state
    const handleSourceUrlChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const scrapeApi = new ScrapeApi()
            const url = e.target.value
            setValue('sourceUrl', url) // Update sourceUrl value in form state

            if (!isMFNLink(url)) return // Only trigger for MFN links
            setScraping(true)
            try {
                const data = await scrapeApi.read(url)
                if (data.title) {
                    setValue('title', data.title)
                }
                if (data.date) {
                    setValue('publishedDate', data.date)
                }
            } catch (error) {
                console.error('Failed to scrape data:', error)
            } finally {
                setScraping(false)
            }
        },
        [setValue],
    )

    const handleSelectPressRelease = (pressRelease: PressReleaseLight | null) => {
        if (!pressRelease) return

        setSelectedPressRelease(pressRelease)
    }

    const handleSubmit = () => {
        onSubmit(formMethods.getValues())
        formMethods.reset(defaultValues)
    }

    if (!entity) {
        return <div>SourceFormRow: Missing entity.</div>
    }

    const transactionType: TransactionType | null = issue
        ? 'Issue'
        : warrant
          ? 'Warrant'
          : convertible
            ? 'Convertible'
            : null
    const issueType = issue ? issue.type : null
    const registeredSourceTypes: SourceType[] = sources?.map((source) => source.sourceType).filter((t) => !!t)
    const singularSourceTypes = issue
        ? singularIssueSourceTypes
        : warrant
          ? singularWarrantSourceTypes
          : singularConvertibleSourceTypes

    return (
        <FormProvider {...formMethods}>
            <TableRow key='sourceForm'>
                <TableCell>
                    {inputMethod === 'internal' ? (
                        <>
                            <Select
                                open={false}
                                onOpen={openSelectPressReleaseDialog}
                                value={''}
                                displayEmpty={true}
                                renderValue={() =>
                                    selectedPressRelease ? selectedPressRelease.title.slice(0, 100) : 'Välj källa'
                                }
                            ></Select>
                            <SelectPressReleaseDialog
                                defaultPressReleaseId={selectedPressRelease?.id ?? null}
                                onClose={handleSelectPressRelease}
                            />
                        </>
                    ) : (
                        <RegisteredTextField
                            label='URL'
                            name='sourceUrl'
                            onChange={handleSourceUrlChange} // Trigger scrape on change
                            validation={{
                                validate: (url: string) => {
                                    const sourceAlreadyExists = sources.some((s) => s.sourceUrl === url)
                                    return !sourceAlreadyExists || 'Källan är redan tillagd'
                                },
                            }}
                        />
                    )}
                </TableCell>
                <TableCell>
                    <RegisteredTextField label={t('entities.issue.fields.internalComment')} name='internalComment' />
                </TableCell>
                <TableCell colSpan={inputMethod === 'manual' ? 0 : 3}>
                    <SourceTypeSelect
                        label={t('site.table.type')}
                        formMethods={formMethods}
                        issueType={issueType}
                        transactionType={transactionType}
                        excludeSourceTypes={intersection(singularSourceTypes, registeredSourceTypes)}
                        validation={{ required: 'Typ måste anges' }}
                    />
                </TableCell>
                {inputMethod === 'manual' && (
                    <>
                        <TableCell>
                            <RegisteredTextField
                                label='Titel'
                                name='title'
                                validation={{ required: 'Titel måste anges' }}
                            />
                        </TableCell>
                        <TableCell>
                            <RegisteredDateTimePicker
                                label='Publicerad'
                                name='publishedDate'
                                minDate={minDate ?? undefined}
                                maxDate={maxDate ?? undefined}
                                validation={{ required: 'Datum måste anges' }}
                            />
                        </TableCell>
                    </>
                )}
                <TableCell>
                    <Button
                        variant='outlined'
                        onClick={(...args) => void formMethods.handleSubmit(handleSubmit)(...args)}
                        disabled={scraping}
                    >
                        Lägg&nbsp;till
                    </Button>
                    {scraping && <Typography variant='caption'>Loading...</Typography>}
                </TableCell>
            </TableRow>
        </FormProvider>
    )
}
